import React from 'react';
import { AlertTriangle, Download } from 'lucide-react';
import { ProblemSolution } from '../components/ProblemSolution';
import { OfferDetails } from '../components/OfferDetails';
import { FAQAndClose } from '../components/FAQAndClose';
import { LeadModalProvider, useLeadModal } from '../components/LeadModalContext';
function SalesPageContent({
  videoId
}: {
  videoId: string;
}) {
  const {
    openModal
  } = useLeadModal();
  return <div className="min-h-screen bg-bunker font-sans selection:bg-pitahaya selection:text-white" data-id="element-208">
      {/* STICKY URGENCY BAR */}
      <div className="sticky top-0 z-40 bg-pitahaya text-white px-4 py-2 shadow-lg border-b border-pitahaya-dark" data-id="element-209">
        <div className="max-w-7xl mx-auto flex flex-row items-center justify-center sm:justify-between gap-3 text-center sm:text-left" data-id="element-210">
          <div className="flex items-center gap-2 min-w-0" data-id="element-211">
            <AlertTriangle className="w-4 h-4 shrink-0 animate-pulse" data-id="element-212" />
            <p className="text-xs md:text-sm font-medium truncate sm:whitespace-normal" data-id="element-213">
              <strong className="font-bold uppercase tracking-wider hidden sm:inline" data-id="element-214">
                Alerta Económica:
              </strong>{' '}
              <span className="hidden md:inline" data-id="element-215">
                Debido a la volatilidad del dólar, mantenemos el precio de Fase
                4 solo por los próximos 12 lotes.
              </span>
              <span className="md:hidden" data-id="element-216">Precio Fase 4 — solo 12 lotes.</span>
            </p>
          </div>
          <button onClick={openModal} className="shrink-0 bg-white text-pitahaya text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full hover:bg-gray-100 transition-colors" data-id="element-217">
            Reservar Cupo
          </button>
        </div>
      </div>

      {/* HERO SECTION — must fit headline + video + CTA above the fold */}
      <section className="relative flex flex-col items-center justify-center px-4 sm:px-6 py-6 sm:py-6 overflow-hidden sm:min-h-[calc(100vh-44px)]" data-id="element-218">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0" data-id="element-219">
          <img src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?q=80&w=2832&auto=format&fit=crop" alt="Yucatan Land" className="w-full h-full object-cover object-center" data-id="element-220" />
          <div className="absolute inset-0 bg-bunker/90 mix-blend-multiply" data-id="element-221"></div>
          <div className="absolute inset-0 bg-bunker/70" data-id="element-222"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center w-full flex flex-col justify-center gap-3 sm:gap-4" data-id="element-223">
          <div className="inline-flex self-center border border-pitahaya/50 bg-pitahaya/10 px-3 py-1 rounded-full" data-id="element-224">
            <span className="text-pitahaya font-sans font-bold text-[10px] sm:text-xs tracking-widest uppercase" data-id="element-225">
              El Refugio Patrimonial 2026
            </span>
          </div>

          <h1 className="font-serif text-[1.35rem] xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-[1.1] tracking-tight" data-id="element-226">
            Saca tus Ahorros del Barco que se Hunde: Protege tu Capital en{' '}
            <span className="text-pitahaya italic" data-id="element-227">"Ladrillos Verdes"</span> que
            Ni la Inflación Ni las Crisis del Dólar Pueden Tocar
          </h1>

          {/* VIMEO VIDEO */}
          <div className="w-full max-w-2xl mx-auto" data-id="element-228">
            <div className="relative rounded-2xl overflow-hidden border-2 border-pitahaya/40 shadow-2xl shadow-pitahaya/20" style={{
            padding: '56.25% 0 0 0',
            position: 'relative'
          }} data-id="element-229">
              <iframe src={`https://player.vimeo.com/video/${videoId}?badge=0&autopause=0&player_id=0&app_id=58479`} frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%'
            }} title="Pithaya Investments VSL" data-id="element-230"></iframe>
            </div>
          </div>

          {/* CTA BUTTON */}
          <div className="flex flex-col items-center gap-1.5" data-id="element-231">
            <button onClick={openModal} className="inline-flex items-center justify-center gap-2 bg-pitahaya hover:bg-pitahaya-dark text-white font-sans font-bold uppercase tracking-wider px-6 sm:px-10 py-3 sm:py-4 text-sm sm:text-base rounded-full transition-all shadow-lg shadow-pitahaya/30 hover:scale-[1.02] active:scale-[0.98]" data-id="element-232">
              <Download className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-233" />
              Descargar Guía Gratuita y Agendar Entrevista
            </button>
            <p className="text-earth/60 text-[10px] sm:text-xs font-sans tracking-wide" data-id="element-234">
              🔒 Acceso inmediato — Sin costo — Plazas limitadas
            </p>
          </div>
        </div>
      </section>

      {/* SUB-INTRO copy moved below the fold */}
      <section className="relative bg-bunker text-earth py-10 sm:py-16 px-6 border-t border-white/5" data-id="element-235">
        <div className="max-w-3xl mx-auto text-center" data-id="element-236">
          <p className="font-serif text-2xl md:text-3xl text-earth/90 leading-relaxed italic mb-6" data-id="element-237">
            "Convierte tu dinero{' '}
            <span className="text-pitahaya not-italic" data-id="element-238">'en papel'</span> en
            Propiedad Privada productiva en Yucatán y asegura un flujo de
            exportación de por vida."
          </p>
          <div className="w-16 h-px bg-pitahaya mx-auto mb-6" data-id="element-239"></div>
          <p className="font-sans text-lg text-earth/70 leading-relaxed" data-id="element-240">
            Mientras el mundo vive en la incertidumbre geopolítica de 2026, la
            tierra fértil de Yucatán sigue produciendo riqueza tangible. El
            búnker financiero que tu familia necesita existe, es real y está a
            tu alcance.
          </p>
        </div>
      </section>

      {/* MAIN CONTENT SECTIONS */}
      <ProblemSolution data-id="element-241" />
      <OfferDetails data-id="element-242" />
      <FAQAndClose data-id="element-243" />

      {/* FOOTER */}
      <footer className="bg-bunker text-earth/40 py-8 text-center border-t border-white/5 font-sans text-sm" data-id="element-244">
        <p data-id="element-245">© 2026 Pitahaya Investments. Todos los derechos reservados.</p>
        <p className="mt-2" data-id="element-246">
          Esta es una página de ventas diseñada para fines ilustrativos de
          blindaje patrimonial.
        </p>
      </footer>
    </div>;
}
export function SalesPage({
  videoId = '1190178521'
}: {
  videoId?: string;
} = {}) {
  return <LeadModalProvider data-id="element-247">
      <SalesPageContent videoId={videoId} data-id="element-248" />
    </LeadModalProvider>;
}